import { z } from "zod";

// Indian mobile: 10 digits starting with 6-9
export const indianMobileRegex = /^[6-9]\d{9}$/;

// Aadhaar: 12 digits, starts with 2-9 (UIDAI standard)
const aadhaarRegex = /^[2-9]\d{11}$/;

const calculateAge = (dob: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  
  return age;
};

// ── Database Table Schemas ───────────────────────────────────────────────────

// PATIENTS
const PatientsSchema = z.object({
  patient_id: z.string().uuid().optional(),
  clinic_id: z.string().uuid().optional(),
  title: z.string().max(20).optional().or(z.string().length(0)),
  first_name: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must not exceed 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "First name can only contain letters, spaces, hyphens, and apostrophes"),
  last_name: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must not exceed 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name can only contain letters, spaces, hyphens, and apostrophes"),
  // Exactly one of date_of_birth / age_years is set — enforced server-side
  // (registration takes either an exact DOB or an approximate age when DOB
  // is unknown).
  date_of_birth: z
    .string()
    .refine((val) => !isNaN(new Date(val).getTime()), "Invalid date format")
    .refine((val) => {
      const age = calculateAge(new Date(val));
      return age >= 0 && age <= 150;
    }, "Age must be between 0 and 150 years")
    .refine((val) => new Date(val) <= new Date(), "Date of birth cannot be in the future")
    .optional(),
  age_years: z.number().min(0).max(130).optional(),
  gender: z.string().refine(
    (val) => val === "Male" || val === "Female" || val === "Other",
    { message: "Gender must be Male, Female, or Other" }
  ),
  blood_group: z.string().max(10).optional().or(z.string().length(0)),
  marital_status: z.string().max(20).optional().or(z.string().length(0)),
  occupation: z.string().max(100).optional().or(z.string().length(0)),
  company: z.string().max(100).optional().or(z.string().length(0)),
  is_active: z.boolean().optional(),
  created_by: z.string().uuid().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
  name: z.string().optional(), // Computed field from API
  display_id: z.string().optional(), // Computed field from API (e.g. "PAT-A3KQ9Z")
  patient_no: z.number().optional(),
  tags: z.array(z.string()).optional(),
});

// PATIENT_CONTACTS
const PatientContactsSchema = z.object({
  contact_id: z.string().uuid().optional(),
  patient_id: z.string().uuid().optional(),
  contact_number: z
    .string()
    .min(1, "Mobile number is required")
    .regex(indianMobileRegex, "Please enter a valid 10-digit Indian mobile number (starting with 6-9)"),
  alternate_contact_number: z
    .string()
    .regex(indianMobileRegex, "Please enter a valid 10-digit Indian mobile number (starting with 6-9)")
    .optional()
    .or(z.string().length(0)),
  email: z
    .string()
    .email("Please enter a valid email address")
    .max(100, "Email must not exceed 100 characters")
    .toLowerCase()
    .optional()
    .or(z.string().length(0)),
  aadhar_number: z
    .string()
    .min(1, "Aadhaar number is required")
    .regex(aadhaarRegex, "Please enter a valid 12-digit Aadhaar number"),
  abha_id: z.string().max(50).optional().or(z.string().length(0)),
});

// PATIENT_ADDRESSES
const PatientAddressesSchema = z.object({
  address_id: z.string().uuid().optional(),
  patient_id: z.string().uuid().optional(),
  address: z
    .string()
    .min(1, "Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(500, "Address must not exceed 500 characters"),
  city: z.string().max(100).optional().or(z.string().length(0)),
  state: z.string().max(100).optional().or(z.string().length(0)),
  pincode: z.string().max(10).optional().or(z.string().length(0)),
});

// INSURANCE
const InsuranceSchema = z.object({
  insurance_id: z.string().uuid().optional(),
  patient_id: z.string().uuid().optional(),
  provider: z.string().max(100).optional().or(z.string().length(0)),
  policy_number: z.string().max(50).optional().or(z.string().length(0)),
  policy_holder_name: z.string().max(100).optional().or(z.string().length(0)),
  policy_expiry_date: z.string().optional().or(z.string().length(0)),
  coverage_amount: z.number().optional().or(z.string().optional()),
});

// MEDICAL_PROFILES
const MedicalProfilesSchema = z.object({
  medical_profile_id: z.string().uuid().optional(),
  patient_id: z.string().uuid().optional(),
  medical_conditions: z.any().optional(), // JSON field
  current_medications: z.string().max(5000).optional().or(z.string().length(0)),
  previous_surgeries: z.string().max(5000).optional().or(z.string().length(0)),
  medical_history_notes: z.string().max(5000).optional().or(z.string().length(0)),
  lifestyle_options: z.any().optional(), // JSON field
});

// PATIENT_PEDIATRIC_PROFILE
// One-time neonatal inputs, registration/edit only - drives WHO vs Fenton
// growth-chart selection. gestational_age_weeks is required when is_preterm
// is true (preterm flag alone can't compute corrected age / select Fenton).
export const PediatricProfileSchema = z
  .object({
    pediatric_profile_id: z.string().uuid().optional(),
    patient_id: z.string().uuid().optional(),
    gestational_age_weeks: z.number().int().min(20).max(45).optional(),
    gestational_age_days: z.number().int().min(0).max(6).optional(),
    birth_weight_grams: z.number().int().min(200).max(8000).optional(),
    is_preterm: z.boolean().optional(),
    multiple_birth: z.boolean().optional(),
    is_down_syndrome: z.boolean().optional(),
  })
  .refine(
    (data) => !data.is_preterm || data.gestational_age_weeks !== undefined,
    {
      message: "Gestational age (weeks) is required for a preterm patient",
      path: ["gestational_age_weeks"],
    }
  );

export type PediatricProfileInput = z.infer<typeof PediatricProfileSchema>;

// PATIENT_PREFERENCES
const PatientPreferencesSchema = z.object({
  preference_id: z.string().uuid().optional(),
  patient_id: z.string().uuid().optional(),
  appointment_confirmations: z.boolean().optional(),
  appointment_reminders: z.boolean().optional(),
  prescription_ready: z.boolean().optional(),
  payment_received: z.boolean().optional(),
  payment_reminders: z.boolean().optional(),
  system_updates: z.boolean().optional(),
  email_frequency: z.string().max(50).optional().or(z.string().length(0)),
  quiet_hours: z.string().max(50).optional().or(z.string().length(0)),
});

// EMERGENCY_CONTACTS
const EmergencyContactsSchema = z.object({
  emergency_contact_id: z.string().uuid().optional(),
  patient_id: z.string().uuid().optional(),
  name: z.string().max(100).optional().or(z.string().length(0)),
  relationship: z.string().max(50).optional().or(z.string().length(0)),
  contact_number: z
    .string()
    .regex(indianMobileRegex, "Please enter a valid 10-digit Indian mobile number (starting with 6-9)")
    .optional()
    .or(z.string().length(0)),
  email: z.string().email().max(100).optional().or(z.string().length(0)),
  address: z.string().max(500).optional().or(z.string().length(0)),
  city: z.string().max(100).optional().or(z.string().length(0)),
  state: z.string().max(100).optional().or(z.string().length(0)),
  pincode: z.string().max(10).optional().or(z.string().length(0)),
  // Guardian designation for pediatric growth-chart WhatsApp share.
  is_guardian: z.boolean().optional(),
  guardian_whatsapp_consent: z.boolean().optional(),
});

// PATIENT_MEDIA
const PatientMediaSchema = z.object({
  media_id: z.string().uuid().optional(),
  patient_id: z.string().uuid().optional(),
  photo_s3_key: z.string().optional().or(z.string().length(0)),
  uploaded_at: z.string().datetime().optional(),
});


// ── Combined Form / API Schemas ──────────────────────────────────────────────

// Complete record (GET /patients/:id)
export const PatientDbSchema = z.object({
  // Core patient fields from patients table
  ...PatientsSchema.shape,
  contact_number: z.string().optional(),
  reschedule_count: z.number().optional(),
  excessive_reschedule_flag: z.boolean().optional(),

  // Nested objects from related tables (as returned by API)
  contact: PatientContactsSchema.nullable().optional(),
  address: PatientAddressesSchema.nullable().optional(),
  insurance: InsuranceSchema.nullable().optional(),
  medical_profile: MedicalProfilesSchema.nullable().optional(),
  pediatric_profile: PediatricProfileSchema.nullable().optional(),
  preferences: PatientPreferencesSchema.nullable().optional(),
  media: PatientMediaSchema.nullable().optional(),
  emergency_contacts: z.array(EmergencyContactsSchema).optional(),
});

export type PatientDbRecord = z.infer<typeof PatientDbSchema>;

// Flat schema for form validation (before transforming to nested for API)
export const PatientCreateFormSchemaBase = z.object({
  first_name: z.string().min(2, "First name must be at least 2 characters").max(50),
  middle_name: z.string().max(50).optional().or(z.string().length(0)),
  last_name: z.string().min(2, "Last name must be at least 2 characters").max(50),
  // Exactly one of these two must be filled — see the .refine() below.
  date_of_birth: z.string().optional().or(z.string().length(0)),
  age_years: z.string().optional().or(z.string().length(0)),
  gender: z.string().refine(
    (val) => ["Male", "Female", "Other"].includes(val),
    { message: "Gender must be Male, Female, or Other" }
  ),
  contact_number: z.string().regex(indianMobileRegex, "Please enter a valid 10-digit Indian mobile number"),
  alternate_contact_number: z.string().regex(indianMobileRegex, "Please enter a valid 10-digit Indian mobile number").optional().or(z.string().length(0)),
  email: z.string().email("Please enter a valid email address").optional().or(z.string().length(0)),
  aadhar_number: z.string().refine(
    (val) => val === "" || aadhaarRegex.test(val),
    { message: "Invalid Aadhaar number — must be exactly 12 digits starting with 2–9" }
  ).optional(),
  abha_id: z.string().optional().or(z.string().length(0)),
  // Structured address fields — concatenated into address string for API
  house_no: z.string().max(50).optional().or(z.string().length(0)),
  building: z.string().max(100).optional().or(z.string().length(0)),
  street: z.string().max(100).optional().or(z.string().length(0)),
  locality: z.string().max(100).optional().or(z.string().length(0)),
  landmark: z.string().max(100).optional().or(z.string().length(0)),
  city: z.string().max(100).optional().or(z.string().length(0)),
  state: z.string().max(100).optional().or(z.string().length(0)),
  pincode: z.string().max(10).optional().or(z.string().length(0)),
  clinic_id: z.string().uuid().optional(),
  tags: z.array(z.string()).optional(),
});

export const PatientCreateFormSchema = PatientCreateFormSchemaBase.refine(
  (d) => Boolean(d.date_of_birth) !== Boolean(d.age_years),
  {
    message: "Provide either a date of birth or an approximate age, not both",
    path: ["date_of_birth"],
  },
);

export type PatientCreateFormInput = z.infer<typeof PatientCreateFormSchema>;

// Patient creation payload (POST /patients) - aligned with backend PatientFullCreate shape
export const PatientCreateSchema = z
  .object({
    clinic_id: z.string().uuid().optional(),
    first_name: z.string().min(1, "First name is required").max(50),
    middle_name: z.string().max(50).optional().or(z.string().length(0)),
    last_name: z.string().min(1, "Last name is required").max(50),
    date_of_birth: z.string().optional().or(z.string().length(0)),
    age_years: z.number().int().min(0).max(130).optional(),
    gender: z.enum(["Male", "Female", "Other"]),
    contact_number: z.string().min(1, "Contact number is required"),
    title: z.string().max(20).optional().or(z.string().length(0)),
    blood_group: z.string().max(10).optional().or(z.string().length(0)),
    marital_status: z.string().max(20).optional().or(z.string().length(0)),
    occupation: z.string().max(100).optional().or(z.string().length(0)),
    company: z.string().max(100).optional().or(z.string().length(0)),
    photo_s3_key: z.string().max(255).optional().or(z.string().length(0)),
    tags: z.array(z.string()).optional(),

    contact: z
      .object({
        contact_number: z.string().optional().or(z.string().length(0)),
        alternate_contact_number: z.string().optional().or(z.string().length(0)),
        email: z.string().email().optional().or(z.string().length(0)),
        aadhar_number: z.string().optional().or(z.string().length(0)),
        abha_id: z.string().optional().or(z.string().length(0)),
      })
      .optional(),

    address: z
      .object({
        address: z.string().optional().or(z.string().length(0)),
        city: z.string().max(100).optional().or(z.string().length(0)),
        state: z.string().max(100).optional().or(z.string().length(0)),
        pincode: z.string().max(10).optional().or(z.string().length(0)),
      })
      .optional(),

    emergency_contacts: z.array(EmergencyContactsSchema).optional(),
    insurance: InsuranceSchema.partial().optional(),
    medical_profile: MedicalProfilesSchema.partial().optional(),
    pediatric_profile: PediatricProfileSchema.optional(),
    preferences: PatientPreferencesSchema.partial().optional(),
    media: PatientMediaSchema.partial().optional(),
  })
  .refine(
    (d) => Boolean(d.date_of_birth) !== (d.age_years !== undefined && d.age_years !== null),
    {
      message: "Provide exactly one of date_of_birth or age_years, not both or neither",
      path: ["date_of_birth"],
    }
  );

export type PatientCreateInput = z.infer<typeof PatientCreateSchema>;

// Patient update payload (PATCH /patients/:id) - grouped by database tables
export const PatientUpdateSchema = z.object({
  patients: PatientsSchema.partial().optional(),
  patient_contacts: PatientContactsSchema.partial().optional(),
  patient_addresses: PatientAddressesSchema.partial().optional(),
  insurance: InsuranceSchema.partial().optional(),
  medical_profiles: MedicalProfilesSchema.partial().optional(),
  patient_pediatric_profile: PediatricProfileSchema.optional(),
  patient_preferences: PatientPreferencesSchema.partial().optional(),
  emergency_contacts: z.array(EmergencyContactsSchema).optional(),
});

export type PatientUpdateInput = z.infer<typeof PatientUpdateSchema>;

// ── Validation Helpers ───────────────────────────────────────────────────────

export const validatePatientCreate = (data: unknown) => {
  return PatientCreateFormSchema.safeParse(data);
};

export const validatePatientUpdate = (data: unknown) => {
  return PatientUpdateSchema.safeParse(data);
};

export const formatValidationErrors = (error: z.ZodError): string[] => {
  return error.issues.map((err) => {
    const field = err.path.join(".");
    return `${field}: ${err.message}`;
  });
};

export const getFirstValidationError = (error: z.ZodError): string => {
  if (error.issues.length > 0) {
    const firstError = error.issues[0];
    return firstError.message;
  }
  return "Validation failed. Please check your input.";
};
